import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

type TranslationEditContextValue = {
  /**
   * When true, `<FormattedMessage>` renders a transparent click target that
   * posts the translation id to the parent window for editing. Off by
   * default — only turned on when the host app runs inside a focalazy iframe
   * AND the handshake succeeds (prevents accidental activation in other
   * embedding contexts).
   */
  enabled: boolean
}

const TranslationEditContext = createContext<TranslationEditContextValue>({ enabled: false })

export const useTranslationEdit = () => useContext(TranslationEditContext)

// Origins we accept as the embedding focalazy parent. Production
// translations.faceup.com, the .dev / .fun preview deployments, and
// localhost (with any port) for local development. Anything else is
// treated as a malicious embedder and its postMessage events are dropped.
const FOCALAZY_PARENT_ORIGIN_RE =
  /^https?:\/\/(([a-zA-Z0-9-]+\.)*faceup\.(com|dev|fun)|localhost(:\d+)?)$/

// Once we've seen a postMessage from a trusted focalazy origin we lock it in
// here and use it as the targetOrigin for all outbound posts (instead of
// '*'). The provider sets it; `postTranslationSelect` reads it. Module-scoped
// is fine — there's at most one TranslationEditProvider mounted per page.
let trustedParentOrigin: string | null = null

/**
 * Activates click-to-edit for `<FormattedMessage>` inside the provider's
 * subtree. Intended to be wrapped only around specific pages (e.g. an
 * investigation detail view) rather than the whole app, so editing mode
 * doesn't leak into chrome (nav, modals) that have their own click handlers.
 *
 * Handshake with the parent frame (focalazy):
 *   1. This provider mounts and posts `{ type: 'focalazy:child-ready' }` upward.
 *   2. Focalazy replies with `{ type: 'focalazy:enable' }` → `enabled` flips to true.
 * The handshake is a safety measure: if the app happens to be iframed by
 * something that isn't focalazy, nothing is activated.
 *
 * Origin hardening: every inbound message must come from
 * `FOCALAZY_PARENT_ORIGIN_RE`. A malicious parent that iframes this app
 * can't flip edit mode on, force reloads, or change the language even if
 * it manages to set `event.source = window.parent`.
 */
export const TranslationEditProvider = ({ children }: { children: ReactNode }) => {
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (window.parent === window) {
      return // not iframed
    }

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) {
        return
      }
      if (!FOCALAZY_PARENT_ORIGIN_RE.test(event.origin)) {
        // Untrusted origin — drop the message silently. Logging would be
        // noisy on pages that happen to receive other cross-frame chatter.
        return
      }
      // Lock in this exact origin so postTranslationSelect / future outbound
      // messages target it precisely instead of using '*'.
      trustedParentOrigin = event.origin

      const data = event.data
      if (!data || typeof data !== 'object') {
        return
      }
      if (data.type === 'focalazy:enable') {
        setEnabled(true)
      } else if (data.type === 'focalazy:disable') {
        setEnabled(false)
      } else if (data.type === 'focalazy:reload') {
        // Parent asked to refresh the current in-iframe route — reload the
        // embedded app without dropping back to the base URL (which a parent
        // cross-origin iframe.src reset would otherwise do).
        window.location.reload()
      } else if (data.type === 'focalazy:refresh-translations') {
        // Soft refresh: refetch translations without losing current route.
        // Broadcast to any listener in this window (e.g. LanguageProvider)
        // that knows how to clear its cache and re-fetch.
        window.dispatchEvent(new CustomEvent('focalazy:refresh-translations'))
      } else if (data.type === 'focalazy:set-language' && typeof data.language === 'string') {
        // Parent editor switched language tabs — push a `language` code
        // (e.g. 'en-US', 'de', 'cs') that the host app turns into its
        // local Language enum and re-renders with. Broadcast via DOM
        // CustomEvent so whatever component owns the language state (e.g.
        // LanguageWrapper) can pick it up without each iframe integrator
        // having to wire parent-window listeners themselves.
        window.dispatchEvent(
          new CustomEvent('focalazy:set-language', { detail: { language: data.language } })
        )
      }
    }

    window.addEventListener('message', onMessage)
    // Announce readiness. We don't know the focalazy origin yet (we can't
    // read window.parent.location.origin cross-origin), so this first
    // message goes to '*'. It carries no sensitive data — it's just a
    // "we exist" beacon — and the next inbound message lets us lock onto
    // the real origin for everything that follows.
    window.parent.postMessage({ type: 'focalazy:child-ready' }, '*')

    return () => {
      window.removeEventListener('message', onMessage)
      // Reset the locked origin on unmount so a remount picks up the next
      // parent fresh (e.g. after a page navigation that swaps the iframe).
      trustedParentOrigin = null
    }
  }, [])

  return (
    <TranslationEditContext.Provider value={{ enabled }}>
      {children}
    </TranslationEditContext.Provider>
  )
}

/**
 * Helper used by `FormattedMessage` to post a click back to the parent
 * focalazy frame. Exported for tests / advanced composition — most callers
 * should rely on the provider + component integration.
 *
 * Uses the origin locked in by the provider's handshake. Falls back to a
 * no-op if no trusted origin has been seen yet — better to drop the message
 * than broadcast translation ids to whatever happens to be the parent.
 */
export const postTranslationSelect = (id: string) => {
  if (typeof window === 'undefined') {
    return
  }
  if (window.parent === window) {
    return
  }
  if (!trustedParentOrigin) {
    return
  }
  window.parent.postMessage({ type: 'focalazy:translate', id }, trustedParentOrigin)
}
